import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faLightbulb, faUserPlus, faStar, faShuffle, faBroom, faCopy, faBook } from '@fortawesome/free-solid-svg-icons'; // Import the lightbulb and user plus icons
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css'; // Import default styles
import './App.css';

// Tooltip Component
const Tooltip = ({ children, text, visible }) => (
  <div className="tooltip" style={{ position: 'relative', display: 'inline-block' }}>
    {children}
    <span className={`tooltip-text${visible ? " visible" : ""}`}>{text}</span>
  </div>
);

let serverURL = 'https://segue.ink/backend';
let countDownStart = 0; //process.env.COUNT_DOWN_START || 5;
if (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'
) {
  serverURL = '';
  countDownStart = 0; //1;
}

function App() {
  const serverPort = process.env.BACKEND_SERVER_PORT || 3001;
  const SECRET_KEY =
    process.env.SECRET_KEY || '&Ui4!p$lY84FrfI@EO^24N&*SSpQk0&HtRcV';

  const [imagePath, setImagePath] = useState('');
  const [caption, setCaption] = useState('');
  const [hint, setHint] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [filterList, setFilterList] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({ all: true });
  const [showHintModal, setShowHintModal] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');

  // New state for score modal
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreMessage, setScoreMessage] = useState('');
  const [ip, setIp] = useState('');
  const [seenImages, setSeenImages] = useState(() => {
    const stored = localStorage.getItem('seenImages');
    return stored ? JSON.parse(stored) : [];
  });
  const [iname, setIname] = useState('');
  const [score, setScore] = useState('');

  // New state for registration modal
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerMessage, setRegisterMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  //new state for shuffle modal
  const [shuffle, setShuffle] = useState(false);

  // Tooltip states and refs for timers
  const [tooltipVisibleMore, setTooltipVisibleMore] = useState(false);
  const tooltipTimeoutMore = useRef(null);

  const [tooltipVisibleFilter, setTooltipVisibleFilter] = useState(false);
  const tooltipTimeoutFilter = useRef(null);

  const [tooltipVisibleHint, setTooltipVisibleHint] = useState(false);
  const tooltipTimeoutHint = useRef(null);

  const [tooltipVisibleRegister, setTooltipVisibleRegister] = useState(false);
  const tooltipTimeoutRegister = useRef(null);

  const [tooltipVisibleScore, setTooltipVisibleScore] = useState(false);
  const tooltipTimeoutScore = useRef(null);

  const [tooltipVisibleShuffle, setTooltipVisibleShuffle] = useState(false);
  const tooltipTimeoutShuffle = useRef(null);

  const buttonStartRef = useRef(null);

  //set up ip
  useEffect(() => {
    fetch('https://api.ipify.org')
      .then(response => response.text())
      .then(data => setIp(data));
  }, []);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutMore.current) clearTimeout(tooltipTimeoutMore.current);
      if (tooltipTimeoutFilter.current) clearTimeout(tooltipTimeoutFilter.current);
      if (tooltipTimeoutHint.current) clearTimeout(tooltipTimeoutHint.current);
      if (tooltipTimeoutRegister.current) clearTimeout(tooltipTimeoutRegister.current);
    };
  }, []);

  //see if we came here from a link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('ln')) {
      const encrypted = params.get('ln');
      try {
        let decrypted = CryptoJS.AES.decrypt(decodeURIComponent(encrypted), SECRET_KEY).toString(CryptoJS.enc.Utf8);
        if (decrypted) {
          //must decrement image number by 1 because backend assumes next image
          const regex = /\d+/;
          const match = regex.exec(decrypted);
          if (match) {
            let id = parseInt(match[0], 10);
            --id;
            decrypted = `image ${id}`;
          }

          setIname(decrypted); // or whatever function loads image based on iname
          fetchImage(false, decrypted); // assumes a fetchImage(iname) function

          // // Remove the image from the seenImages list to mark as not seen is needed
          // setSeenImages(prev => {
          //   const updated = prev.filter(img => img !== decrypted);
          //   localStorage.setItem('seenImages', JSON.stringify(updated));
          //   return updated;
          // });
        }
      } catch (e) {
        console.error('Decryption failed', e);
      }
    }
  }, []);

  //first segue
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('ln')) {
      //only do this if we didn't come from a link
      for (let i = 1; i <= 2048; i++) {
        var candidate = `image ${i}`;
        if (!seenImages.includes(candidate)) {
          candidate = `image ${i - 1}`;
          setIname(candidate);
          fetchImage(false, candidate);
          break;
        }
      }
    }
  }, []); // only once at start


  // Fetch filter list on mount and when the filter modal is opened
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const cacheBuster = new Date().getTime(); // Generate a unique value for cache-busting
        let response;
        const url = serverURL === ''
          ? `http://localhost:${serverPort}/tag-list?cb=${cacheBuster}`
          : `${serverURL}/tag-list?cb=${cacheBuster}`;

        response = await axios.get(url, {
          responseType: 'json',
          withCredentials: true,
        });

        if (response.status === 200 && Array.isArray(response.data)) {
          setFilterList(response.data);
        } else {
          console.warn("Unexpected response structure:", response.data);
        }
      } catch (error) {
        console.error('Error fetching filter list:', error);
      }
    };

    // Fetch filters when the modal is opened
    if (showFilterModal) {
      fetchFilters();
    }
  }, [showFilterModal]); // Dependency array includes showFilterModal

  const fetchPreviousImage = () => { fetchImage(true); }
  const fetchNextImage = () => { fetchImage(false); }

  const fetchImage = async (previous, passedIname) => {
    const reaIname = iname || passedIname;
    if (!reaIname && !shuffle) return;
    setLoading(true);
    try {
      const selectedTags = Object.keys(selectedFilters).filter(
        (tag) => selectedFilters[tag]
      );

      // Extract the fd parameter from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const fd = urlParams.get('fd'); // Get the fd parameter

      // Construct the filter query
      const filterQuery =
        selectedTags.length > 0
          ? `?filters=${encodeURIComponent(JSON.stringify(selectedTags))}`
          : '';

      // If fd exists, append it to the filter query
      const finalQuery = fd ? `${filterQuery}${filterQuery ? '&' : '?'}fd=${fd}` : filterQuery;

      // Set the server URL based on serverURL, shuffle parameter and the previous parameter
      var url = serverURL === ''
        ? `http://localhost:${serverPort}`
        : `${serverURL}`;

      const path = shuffle === true
        ? "/next-image"
        : "/image/" + reaIname + (previous === true ? '?prev=1' : '');

      url += `${path}${finalQuery}`;
      console.log('URL', url);
      const response = await axios.get(url, {
        responseType: 'json',
        withCredentials: true,
      });

      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const encryptedData = response.data.data;
      const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
      const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      const { imageUrl, caption, hint, looped, name } = decryptedData;
      setImagePath(imageUrl);
      setCaption(caption);
      setHint(hint);
      if (looped) {
        setCountdown(0);
      } else {
        //startCountdown();
      }
      handleNewImageName(name); //setup image name for scoring
      //console.log('iName', iname);
    } catch (error) {
      console.error('Error fetching the image:', error);
      alert(`Failed to fetch image: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // Countdown logic
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const startCountdown = () => {
    setCountdown(countDownStart);
    setTooltipVisibleMore(true);

    if (tooltipTimeoutMore.current) clearTimeout(tooltipTimeoutMore.current);
    tooltipTimeoutMore.current = setTimeout(() => {
      setTooltipVisibleMore(false);
    }, 1);
  };

  // Utility functions: Save and load seen image names
  const [isSeen, setIsSeen] = useState(false);
  const handleNewImageName = (newName) => {
    setIname(newName);

    if (!seenImages.includes(newName)) {
      const updated = [...seenImages, newName];
      setSeenImages(updated);
      localStorage.setItem('seenImages', JSON.stringify(updated));
      setIsSeen(false);  // or true if you want the checkbox unchecked for new images
    } else {
      setIsSeen(true);
    }
  };

  // Tooltip handlers for "More please..." button
  const handleMouseEnterMore = () => {
    setTooltipVisibleMore(true);
    if (tooltipTimeoutMore.current) clearTimeout(tooltipTimeoutMore.current);
    tooltipTimeoutMore.current = setTimeout(() => setTooltipVisibleMore(false), 1);
  };
  const handleMouseLeaveMore = () => {
    // Do nothing; tooltip will hide after timer
  };

  // Tooltip handlers for filter icon
  const handleMouseEnterFilter = () => {
    setTooltipVisibleFilter(true);
    if (tooltipTimeoutFilter.current) clearTimeout(tooltipTimeoutFilter.current);
    tooltipTimeoutFilter.current = setTimeout(() => setTooltipVisibleFilter(false), 1);
  };
  const handleMouseLeaveFilter = () => {
    // Do nothing; tooltip will hide after timer
  };

  // Tooltip handlers for hint icon
  const handleMouseEnterHint = () => {
    setTooltipVisibleHint(true);
    if (tooltipTimeoutHint.current) clearTimeout(tooltipTimeoutHint.current);
    tooltipTimeoutHint.current = setTimeout(() => setTooltipVisibleHint(false), 1);
  };
  const handleMouseLeaveHint = () => {
    // Do nothing; tooltip will hide after timer
  };

  // Tooltip handlers for register icon
  const handleMouseEnterRegister = () => {
    setTooltipVisibleRegister(true);
    if (tooltipTimeoutRegister.current) clearTimeout(tooltipTimeoutRegister.current);
    tooltipTimeoutRegister.current = setTimeout(() => setTooltipVisibleRegister(false), 1);
  };
  const handleMouseLeaveRegister = () => {
    // Do nothing; tooltip will hide after timer
  };


  //tooltip handlers for score
  const handleMouseEnterScore = () => {
    setTooltipVisibleScore(true);
    if (tooltipTimeoutScore.current) clearTimeout(tooltipTimeoutScore.current);
    tooltipTimeoutScore.current = setTimeout(() => setTooltipVisibleScore(false), 1);
  };

  const handleMouseLeaveScore = () => {
    // Do nothing; tooltip will hide after timer
  };
  //tooltip handlers for shuffle
  const handleMouseEnterShuffle = () => {
    setTooltipVisibleShuffle(true);
    if (tooltipTimeoutShuffle.current) clearTimeout(tooltipTimeoutShuffle.current);
    tooltipTimeoutShuffle.current = setTimeout(() => setTooltipVisibleShuffle(false), 1);
  };

  const handleMouseLeaveShuffle = () => {
    // Do nothing; tooltip will hide after timer
  };
  const handleFilterChange = (filter) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filter]: !prev[filter],
      all: false,
    }));
  };

  const handleSaveFilters = () => {
    setShowFilterModal(false);
  };

  //copy segue link
  const handleCopyLink = () => {
    if (!iname) return; // Assume iname is available in your component state/props
    const encrypted = CryptoJS.AES.encrypt(iname, SECRET_KEY).toString();
    const url = new URL(window.location.href);  // Build link
    // Remove other GET variables for cleanliness (except what you want to keep)
    url.search = ''; // clear all
    url.searchParams.set('ln', encodeURIComponent(encrypted));
    // Copy link to clipboard
    navigator.clipboard.writeText(url.toString()).then(() => {
      setCopyMessage('Copied!');
      setTimeout(() => setCopyMessage(''), 1500);
    });
  };

  //start button
  useEffect(() => {
    // Simulate a button click when the component mounts
    if (buttonStartRef.current) {
      buttonStartRef.current.click();
    }
  }, []); // Empty dependency array means this effect runs once after the initial render


  return (
    <div className="App">
      <div className="Content">
        <div className="Header" style={{ marginBottom: '0px' }}>
          <div className="Header-row">
            <a href="https://segue.ink" target="_blank" rel="noopener noreferrer">
              <img
                src={require('./logo.png')}
                alt="Segue.ink"
                className="App-logo"
                onClick={() => {
                  window.location.href = 'https://segue.ink';
                }}
              />
            </a>
            <Tooltip
              text={hint ? "I'd like a mint please" : "No mint available"}
              visible={tooltipVisibleHint}
            >
              <FontAwesomeIcon
                icon={faLightbulb}
                className={`Hint-icon ${hint ? '' : 'dimmed'}`}
                onClick={() => {
                  if (hint) {
                    setShowHintModal(true);
                  }
                }}
                onMouseEnter={handleMouseEnterHint}
                onMouseLeave={handleMouseLeaveHint}
              />
            </Tooltip>

            <Tooltip
              text="Different segues please"
              visible={tooltipVisibleFilter}
            >
              <FontAwesomeIcon
                icon={faFilter}
                className="Filter-icon"
                onClick={() => setShowFilterModal(true)}
                onMouseEnter={handleMouseEnterFilter}
                onMouseLeave={handleMouseLeaveFilter}
              />
            </Tooltip>

            <Tooltip
              text="Get notified of new Segues"
              visible={tooltipVisibleRegister}
            >
              <FontAwesomeIcon
                icon={faUserPlus}
                className="Register-icon"
                onClick={() => setShowRegisterModal(true)}
                onMouseEnter={handleMouseEnterRegister}
                onMouseLeave={handleMouseLeaveRegister}
                style={{ marginLeft: '10px' }}
              />
            </Tooltip>

            <Tooltip
              text="Score a segue"
              visible={tooltipVisibleScore}
            >
              <FontAwesomeIcon
                icon={faStar} // Using the score icon from Awesome Font
                className="Score-icon"
                onClick={() => setShowScoreModal(true)}
                onMouseEnter={handleMouseEnterScore}
                onMouseLeave={handleMouseLeaveScore}
                style={{ marginLeft: '10px' }}
              />
            </Tooltip>

            <Tooltip
              text="Forget visited segues"
            >
              <FontAwesomeIcon
                icon={faBroom} // Using the score icon from Awesome Font
                className="Score-icon"
                onClick={() => {
                  localStorage.removeItem("seenImages");
                  setSeenImages([]);
                  setIsSeen(false);
                }}
                // onMouseEnter={handleMouseEnterShuffle}
                // onMouseLeave={handleMouseLeaveShuffle}
                style={{ marginLeft: '10px' }}
              />
            </Tooltip>

            <Tooltip
              text={shuffle ? "Don't shuffle segues" : "Shuffle segues"}
              visible={tooltipVisibleShuffle}
            >
              <FontAwesomeIcon
                icon={faShuffle} // Using the score icon from Awesome Font
                className={`Shuffle-icon ${shuffle ? 'brightened' : 'dimmed'}`}
                onClick={() => setShuffle(!shuffle)}
                onMouseEnter={handleMouseEnterShuffle}
                onMouseLeave={handleMouseLeaveShuffle}
                style={{ marginLeft: '10px' }}
              />
            </Tooltip>
            <Tooltip
              text="Copy link to this Segue"
            >
              <FontAwesomeIcon
                icon={faCopy} // Using the score icon from Awesome Font
                className="copy-icon"
                onClick={handleCopyLink}
                style={{ marginLeft: '10px' }}
              />
            </Tooltip>
            <Tooltip text="The SEGUE book">
              <a
                href="https://www.amazon.com/dp/B0HD17H8KQ"
                target="_blank"
                rel="noopener noreferrer"
                className="Book-icon"
                aria-label="The SEGUE book"
              >
                <FontAwesomeIcon icon={faBook}
                  style={{ color: 'black', marginLeft: '10px' }}
                />
              </a>
            </Tooltip>

          </div>
          <div className="App-byline">
            Moving smoothly from one state or
            situation to another via ink.
          </div>
        </div>
        <div>
          {imagePath && (
            <div className="Image-container">
              <div className="resizable-wrapper">
                <ResizableBox width={400} height={400} minConstraints={[100, 100]} maxConstraints={[800, 600]}
                  lockAspectRatio={true}
                  resizeHandles={['se']}
                >
                  <img src={imagePath} alt="Fetched" className="Image-1" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </ResizableBox>
              </div>
              <div style={{ marginBottom: '8px' }}
                className="Caption-1"
                dangerouslySetInnerHTML={{ __html: caption }}
              >
              </div>
            </div>
          )}
              <div className="Book-link">
                <a
                  href="https://www.amazon.com/dp/B0HD17H8KQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    fontSize: "12px",
                    fontFamily: "Comic Sans MS",
                    color: "#999",
                    margin: "0px",
                    padding: "0px" 
                  }}
                >
                  See more Segues in the book →
                </a>
              </div>
          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {!shuffle && (
              <button
                className="CommonButton"
                onClick={fetchPreviousImage}
                disabled={loading || countdown > 0}
                onMouseEnter={handleMouseEnterMore}
                onMouseLeave={handleMouseLeaveMore}
                style={{ marginRight: '10px', width: '150px' }}
              >
                Previous Segue
              </button>
            )}
            <button
              ref={buttonStartRef} //attach ref to button
              className="CommonButton"
              onClick={fetchNextImage}
              disabled={loading || countdown > 0}
              onMouseEnter={handleMouseEnterMore}
              onMouseLeave={handleMouseLeaveMore}
              style={{ width: '150px' }}
            >
              {loading ? (
                'Loading...'
              ) : false /*countdown > 0*/ ? ( //remove ponder q while
                <span>
                  Stop, and ponder a while{' '}
                  <span style={{ fontSize: '0.5em', width: '150px' }}>{countdown}s</span>
                </span>
              ) : (
                'Next Segue'
              )}
            </button>
          </div>
        </div>
        <div style={{ marginTop: '10px' }}>
          <label className="visited-rounded-checkbox-label">
            <input
              type="checkbox"
              checked={isSeen}
              readOnly
              className="visited-rounded-checkbox"
            />
            You've seen this
          </label>
        </div>
      </div>
      <footer className="Footer">
        <a href="https://segue.ink" target="_blank" rel="noopener noreferrer">
          © {new Date().getFullYear()} segue.ink. All rights reserved.
        </a>
      </footer>
      {showFilterModal && (
        <div className="Modal">
          <div className="Modal-content">
            <span
              className="Close"
              onClick={() => setShowFilterModal(false)}
            >
              &times;
            </span>
            <h2>Filter Options</h2>
            <div className="Filter-header">Include Segues</div>
            <div className="Filter-list">
              <div>
                <input
                  type="checkbox"
                  id="filter-all"
                  checked={selectedFilters.all}
                  onChange={() => {
                    setSelectedFilters({ all: true });
                  }}
                />
                <label htmlFor="filter-all">All</label>
              </div>
              {filterList.map((filter, index) => (
                <div key={index}>
                  <input
                    type="checkbox"
                    id={`filter-${index}`}
                    checked={selectedFilters[filter] || false}
                    onChange={() => handleFilterChange(filter)}
                  />
                  <label htmlFor={`filter-${index}`}>{filter}</label>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <button
                className="CommonButton"
                onClick={() => setShowFilterModal(false)}
              >
                Cancel
              </button>
              <button className="CommonButton" onClick={handleSaveFilters}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showHintModal && (
        <div className="Modal">
          <div className="Modal-content">
            <span className="Close" onClick={() => setShowHintModal(false)}>
              &times;
            </span>
            <h2>Mint</h2>
            <p>{hint}</p>
          </div>
        </div>
      )}

      {showScoreModal && (
        <div className="Modal">
          <div className="Modal-score-content">
            <span
              className="Close"
              onClick={() => setShowScoreModal(false)}
            >
              &times;
            </span>
            <h2>Rate this segue</h2>
            <form>
              <div className="radio-button-group">
                <div className="grid-container">
                  <div className="numbers-row">
                    <span>1</span>
                    <span>2</span>
                    <span>3</span>
                    <span>4</span>
                    <span>5</span>
                    <span>6</span>
                    <span>7</span>
                    <span>8</span>
                    <span>9</span>
                    <span>10</span>
                  </div>
                  <div className="radio-buttons-row">
                    <input type="radio" id="rating-1" name="rating" value="1" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-2" name="rating" value="2" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-3" name="rating" value="3" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-4" name="rating" value="4" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-5" name="rating" value="5" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-6" name="rating" value="6" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-7" name="rating" value="7" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-8" name="rating" value="8" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-9" name="rating" value="9" onChange={(e) => setScore(e.target.value)} />
                    <input type="radio" id="rating-10" name="rating" value="10" onChange={(e) => setScore(e.target.value)} />
                  </div>
                </div>
              </div>
              <button className="CommonButton" style={{ marginTop: "5px", marginBottom: "20px" }}
                onClick={async () => {
                  setScoreLoading(true); // Set loading state
                  setScoreMessage(''); // Clear previous messages
                  try {
                    const urlScore = serverURL === ''
                      ? `http://localhost:${serverPort}/score`
                      : `${serverURL}/score`;
                    const response = await axios.post(`${urlScore}`, {
                      ip,
                      iname,
                      score,
                    });
                    //console.log('Response:', response); // Log the response for debugging
                    if (response.status === 200 || response.status === 201) {
                      setRegisterMessage('Segue rated'); // Success message
                      //console.log('Closing modal after successful ratin'); // Log before closing
                      setTimeout(() => {
                        setShowScoreModal(false); // Close the modal after a delay
                      }, 1000); // Close after 1 seconds
                      // setIp('');
                      // setIname('');
                      setScore('');
                    }
                  } catch (error) {
                    console.error('Error during rating:', error);
                    setRegisterMessage('Failed to rate. Please try again.'); // Error message
                  } finally {
                    setScoreLoading(false); // Reset loading state
                  }
                }}
                disabled={scoreLoading} // Disable button while loading
              >
                {scoreLoading ? 'Rating...' : 'Rate'}
              </button>
            </form>
            <a
              style={{
                marginTop: "20px",
                textDecoration: "none", // Remove underline
                color: "#303030", // Set the color (you can change this to any color you prefer)
                //fontFamily: "Arial, sans-serif", // Set the font family (you can change this to any font you prefer)
                fontSize: "14px", // Set the font size (you can change this to any size you prefer)
              }}
              href="mailto:info@segue.ink"
            >
              Send segue.ink some comments?
            </a>          </div>
        </div>
      )}

      {showRegisterModal && (
        <div className="Modal">
          <div className="Modal-content">
            <span className="Close" onClick={() => setShowRegisterModal(false)}>
              &times;
            </span>
            <h2>Register</h2>
            <h3>We'll notify you when there are any new segues</h3>
            <div>
              <input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {registerMessage && <p>{registerMessage}</p>} {/* Display message */}
            <div
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <button
                className="CommonButton"
                onClick={() => setShowRegisterModal(false)}
              >
                Cancel
              </button>
              <button
                className="CommonButton"
                onClick={async () => {
                  setRegisterLoading(true); // Set loading state
                  setRegisterMessage(''); // Clear previous messages
                  try {
                    const urlRegister = serverURL === ''
                      ? `http://localhost:${serverPort}/subscribe`
                      : `${serverURL}/subscribe`;
                    const response = await axios.post(`${urlRegister}`, {
                      name,
                      email,
                    });
                    //console.log('Response:', response); // Log the response for debugging
                    if (response.status === 200 || response.status === 201) {
                      setRegisterMessage('Registration successful! Please check your email to confirm.'); // Success message
                      //console.log('Closing modal after successful registration'); // Log before closing
                      setTimeout(() => {
                        setShowRegisterModal(false); // Close the modal after a delay
                      }, 3000); // Close after 2 seconds
                      setName('');
                      setEmail('');
                    }
                  } catch (error) {
                    console.error('Error during registration:', error);
                    setRegisterMessage('Failed to register. Please try again.'); // Error message
                  } finally {
                    setRegisterLoading(false); // Reset loading state
                  }
                }}
                disabled={registerLoading} // Disable button while loading
              >
                {registerLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
